import {useEffect, useMemo, useRef, useState} from "react";
import {
  Button,
  Card,
  Col,
  Collapse,
  Container,
  Form,
  Row,
  Spinner
} from "react-bootstrap";
import DefaultLayout from "../layouts/DefaultLayout";
import useTranslation from "../hooks/translations";
import {TrendingUp} from "react-feather";
import MonthNavigator from "../components/MonthNavigator";
import {useApi} from "../contexts/ApiProvider";
import IncomeService from "../services/IncomeService";
import {useDateRange} from "../contexts/DateRangeProvider";
import SimpleDataTable from "../components/SimpleDataTable";
import IncomeModal from "./modals/IncomeModal";
import ConfirmationModal from "./modals/ConfirmationModal";
import DatesUtils from "../utils/dates";
import FileSaver from 'file-saver';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import DateRangeField from "../components/DateRangeField";
import SelectField from "../components/SelectField";
import StringUtils from "../utils/StringUtils";
import CurrencyUtils from "../utils/CurrencyUtils";
import IncomeTypeService from "../services/IncomeTypeService";
import IncomeCategoryService from "../services/IncomeCategoryService";
import ProtectedContent from "../components/ProtectedContent";

export default function IncomesPage() {
  const translations = useTranslation();
  const {from, to} = useDateRange();

  const pageTitle = translations.incomes;
  const pageSubtitle = translations.incomesPageSubtitle;

  const api = useApi()
  const [isLoading, setIsLoading] = useState(false);
  const incomeService = useMemo(() => new IncomeService(api), [api]);
  const incomeTypeService = useMemo(() => new IncomeTypeService(api), [api]);
  const incomeCategoryService = useMemo(() => new IncomeCategoryService(api), [api]);

  const columns = [
    {
      name: translations.date,
      data_identifier: "dateFormatted"
    },
    {
      name: translations.amount,
      data_identifier: "amountFormatted"
    },
    {
      name: translations.description,
      data_identifier: "description"
    },
    {
      name: translations.incomeType,
      data_identifier: "type"
    },
    {
      name: translations.incomeCategory,
      data_identifier: "category"
    }
  ];

  const [rows, setRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState({});

  const [openIncomeModal, setOpenIncomeModal] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const [openFilters, setOpenFilters] = useState(false);

  const [incomeTypeOptions, setIncomeTypeOptions] = useState([]);
  const [selectedIncomeTypeOption, setSelectedIncomeTypeOption] = useState("");
  const handleIncomeTypeOptionChange = (option) => {
    setSelectedIncomeTypeOption(option.currentTarget.value);
  };

  const [incomeCategoryOptions, setIncomeCategoryOptions] = useState([]);
  const [filteredIncomeCategoryOptions, setFilteredIncomeCategoryOptions] = useState([]);
  const [selectedIncomeCategoryOption, setSelectedIncomeCategoryOption] = useState("");
  const handleIncomeCategoryOptionChange = (option) => {
    setSelectedIncomeCategoryOption(option.currentTarget.value);
  };

  const [dateFrom, setDateFrom] = useState(from);
  const [dateTo, setDateTo] = useState(to);

  const dateFromField = useRef();
  const dateToField = useRef();
  const typeField = useRef();
  const categoryField = useRef();
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const getIncomeTypes = async () => {
      const response = await incomeTypeService.getAll();
      const typeOptions = response.body.map((item) => {
        return {
          value: item.id,
          name: item.name
        };
      });
      setIncomeTypeOptions(typeOptions);
    }
    (async () => {
      await getIncomeTypes();
    })();
  }, [incomeTypeService]);

  useEffect(() => {
    const getIncomeCategories = async () => {
      const response = await incomeCategoryService.getAll();
      const categoryOptions = response.body.map((item) => {
        return {
          value: item.id,
          name: item.name,
          type: item.type.id
        };
      });
      setIncomeCategoryOptions(categoryOptions);
      setFilteredIncomeCategoryOptions(categoryOptions)
    }
    (async () => {
      await getIncomeCategories();
    })();
  }, [incomeCategoryService]);

  useEffect(() => {
    (async () => {
      await getData(from, to, selectedIncomeTypeOption, selectedIncomeCategoryOption);
    })();
  }, [from, to]);

  useEffect(() => {
    if (!selectedIncomeTypeOption) {
      setFilteredIncomeCategoryOptions(incomeCategoryOptions);
      setSelectedIncomeCategoryOption("");
      return;
    }

    setFilteredIncomeCategoryOptions(incomeCategoryOptions.filter(
      (option) => option.type === Number(selectedIncomeTypeOption))
    );
  }, [selectedIncomeTypeOption]);

  const getData = async (start, end, typeId = undefined, categoryId = undefined) => {
    setIsLoading(true);
    const response = await incomeService.search(start, end, typeId, categoryId);
    const formatedResponse = response.body.map((row) => {
      const dateObject = new Date(row.timestamp);
      const currencyFormatter = CurrencyUtils.getCurrencyFormatter(
        row.currency.symbol
      );
      return {
        id: row.id,
        description: row.description,
        type: row.type.name,
        typeId: row.type.id,
        category: row.category.name,
        categoryId: row.category.id,
        amountFormatted: currencyFormatter.format(row.amount),
        amount: row.amount,
        date: DatesUtils.formatDate(dateObject),
        dateFormatted: DatesUtils.format(dateObject),
        currencyId: row.currency.id,
      }
    });
    setRows(formatedResponse);
    setIsLoading(false);
  }

  // datatable callbacks
  const onEditClick = async (data) => {
    setSelectedRow(data);
    setOpenIncomeModal(true);
  }

  const onDeleteClick = async (data) => {
    setSelectedRow(data);
    setOpenConfirmationModal(true);
  }

  const onAddClick = async () => {
    setSelectedRow({});
    setOpenIncomeModal(true);
  }

  const onExportClick = async () => {
    const csvResponse = await incomeService.export(from, to, selectedIncomeTypeOption, selectedIncomeCategoryOption);
    if (!csvResponse.ok) {
      return
    }
    const csvData = new Blob([csvResponse.body], {type: 'text/csv;charset=utf-8;'});
    const filename = csvResponse.rawResponse.headers.get('Content-Disposition').split('filename=')[1];
    FileSaver.saveAs(csvData, filename ? filename : "csv_export");
  }

  // income modal callbacks
  const onModalClose = async () => {
    setOpenIncomeModal(false);
  }
  const onModalSubmit = async (data) => {
    await getData(from, to, selectedIncomeTypeOption, selectedIncomeCategoryOption);
    setOpenIncomeModal(false);
  }

  // confirmation modal callbacks
  const onConfirmationModalSubmit = async (data) => {
    await incomeService.delete(data.id)
    await getData(from, to, selectedIncomeTypeOption, selectedIncomeCategoryOption);
    setOpenConfirmationModal(false)
  }

  const onConfirmationModalClose = async () => {
    setOpenConfirmationModal(false);
  }

  const onSearch = async (event) => {
    event.preventDefault();

    const from = new Date(dateFromField.current.value);
    const to = new Date(dateToField.current.value);

    setDateFrom(from);
    setDateTo(to);

    await getData(from, to, selectedIncomeTypeOption, selectedIncomeCategoryOption);
  }

  const onClearSearch = async (event) => {
    setDateFrom(from);
    setDateTo(to);
    setSelectedIncomeTypeOption("");
    setSelectedIncomeCategoryOption("");

    await getData(from, to, "", "");
  }

  const totalIncomes = useMemo(
    () => {
      const total = rows.reduce((accumulatedIncomes, currentIncome) => {
        return accumulatedIncomes + currentIncome.amount
      }, 0);

      const currencyFormatter = CurrencyUtils.getCurrencyFormatter(
        !rows || rows.length === 0 ? undefined : rows[0].currencySymbol
      );

      return currencyFormatter.format(total);
    }, [rows]);

  return (
    <DefaultLayout icon={<TrendingUp/>}
                   title={pageTitle}
                   subtitle={pageSubtitle}>
      <Container fluid="xl" className="px-4 mt-n10">
        <Card className="mb-4">
          <Card.Header>
            <Container fluid>
              <Row>
                <Col style={{paddingLeft: 0, paddingRight: 0}}>
                  <MonthNavigator/>
                </Col>
                <Col style={{paddingLeft: 0, paddingRight: 0}}>
                  <div className="p-2 d-flex gap-3 flex-wrap"
                       style={{float: "right"}}>
                    <ProtectedContent allowedRoles={["Admin", "Moderator"]}>
                      <Button
                        variant={openFilters ? "primary" : "outline-primary"}
                        onClick={() => setOpenFilters(!openFilters)}
                        aria-controls="costs-filters-area"
                        aria-expanded={openFilters}
                      >
                        <FontAwesomeIcon icon="filter" className="me-2"/>
                        {translations.filters}
                      </Button>
                    </ProtectedContent>
                    <ProtectedContent allowedActions={["export_income"]}>
                      <Button variant="outline-primary"
                              onClick={onExportClick}>
                        <FontAwesomeIcon icon="file-export"
                                         className="me-2"/>
                        {translations.export}
                      </Button>
                    </ProtectedContent>
                    <ProtectedContent allowedActions={["create_income"]}>
                    <Button variant="primary" onClick={onAddClick}>
                      <FontAwesomeIcon icon="add" className="me-2"/>
                      {translations.add}
                    </Button>
                    </ProtectedContent>
                  </div>
                </Col>
              </Row>
            </Container>
          </Card.Header>
          <Card.Body>
            <Collapse in={openFilters}>
              <div id="costs-filters-area">
                <Card className="mb-4">
                  <Card.Header>{translations.searchIncomes}</Card.Header>
                  <Card.Body>
                    <Form onSubmit={onSearch} id="searchIncomesForm">
                      <div className="mb-3">
                        <DateRangeField
                          name="inputDateRange"
                          label={translations.date}
                          placeholder={translations.enterDate}
                          error={formErrors.date}
                          startDate={dateFrom}
                          endDate={dateTo}
                          fromFieldRef={dateFromField}
                          toFieldRef={dateToField}
                        />
                      </div>
                      <div className="mb-3">
                        <SelectField
                          name="incomeType"
                          label={translations.type}
                          placeholder={translations.enterType}
                          options={incomeTypeOptions}
                          selectedOption={selectedIncomeTypeOption}
                          handleChange={handleIncomeTypeOptionChange}
                          error={formErrors.type}
                          fieldRef={typeField}
                        />
                      </div>
                      <div className="mb-3">
                        <SelectField
                          name="incomeCategory"
                          label={translations.category}
                          placeholder={translations.enterCategory}
                          options={filteredIncomeCategoryOptions}
                          selectedOption={selectedIncomeCategoryOption}
                          handleChange={handleIncomeCategoryOptionChange}
                          error={formErrors.category}
                          fieldRef={categoryField}
                        />
                      </div>
                      <div className="d-flex flex-row-reverse">
                        <Button
                          variant="primary"
                          type="submit"
                          form="searchIncomesForm"
                        >
                          <FontAwesomeIcon icon="magnifying-glass"
                                           className="me-2"/>
                          {translations.search}
                        </Button>
                        <Button
                          variant="outline-primary"
                          type="button"
                          className="me-2"
                          onClick={onClearSearch}
                        >
                          <FontAwesomeIcon icon="xmark" className="me-2"/>
                          {translations.resetSearch}
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              </div>
            </Collapse>
            <Card>
              <Container fluid style={{paddingLeft: 0, paddingRight: 0}}>
                <Card.Header>
                  <Row>
                    <Col
                      className="d-flex justify-content-start">
                      {StringUtils.format(
                        translations.incomesSearchResults,
                        rows.length,
                        DatesUtils.format(dateFrom),
                        DatesUtils.format(dateTo)
                      )}
                    </Col>
                    <Col sm="auto" style={{height: "1rem"}}></Col>
                    <Col sm="auto"
                         className="d-flex justify-content-start">
                  <span>
                  {StringUtils.format(translations.total, totalIncomes)}
                </span>
                    </Col>
                  </Row>
                </Card.Header>
              </Container>
              <Card.Body>
                {isLoading ? <Spinner/> : <SimpleDataTable
                  columns={columns}
                  rows={rows}
                  editAction={onEditClick}
                  deleteAction={onDeleteClick}/>}
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>
      </Container>
      {openIncomeModal && <IncomeModal data={selectedRow}
                                       showModal={openIncomeModal}
                                       onModalSubmit={onModalSubmit}
                                       onModalClose={onModalClose}/>
      }
      {openConfirmationModal &&
        <ConfirmationModal data={selectedRow}
                           onConfirmationModalSubmit={onConfirmationModalSubmit}
                           onConfirmationModalClose={onConfirmationModalClose}
                           showModal={openConfirmationModal}/>
      }
    </DefaultLayout>
  );
}
